/**
 * Learn Mode — Radial Comparative Matrix
 *
 * Inspired by "Colours in Culture" by David McCandless / Information is Beautiful.
 *
 * A full-viewport SVG radial matrix where:
 *   - Angular spokes represent signal clusters from the field (grouped by category)
 *   - 10 concentric rings represent the Learn Battery dimensions (A–J)
 *   - Colored arc cells show each cluster's post-activation state on each dimension
 *   - A legend decodes numbers → cluster names, letters → dimensions
 *
 * Validate asks: "Is it real?"
 * Learn asks:    "What did it change?"
 *
 * Not a sidebar. Not a dashboard. A comparative instrument.
 */

import * as THREE from 'three';
import type { FieldCamera } from '../scene/camera';
import type { NodeRenderer } from '../scene/nodes';
import type { ZoneNavigation } from '../scene/zone';
import { useAuraStore } from '../state/store';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import { CATEGORIES } from '../data/synthetic';
import {
  generateLearnBatteries,
  LEARN_DIMENSIONS,
  LEARN_DIMENSION_LETTERS,
  LEARN_DIMENSION_LABELS,
  LEARN_DIMENSION_DESCRIPTIONS,
  LEARN_STATE_DISPLAY,
  LEARN_STATE_COLORS,
  LEARN_COLOR_LEGEND,
  type LearnBattery,
  type LearnDimension,
  type ClusterLearning,
} from '../data/learning';
import { attachTooltip, hideTooltip } from './tooltip';


// ─── SVG LAYOUT ─────────────────────────────────────────────────

const SVG_SIZE = 800;
const CX = SVG_SIZE / 2;
const CY = SVG_SIZE / 2;
const INNER_R = 70;
const OUTER_R = 338;
const RING_COUNT = LEARN_DIMENSIONS.length; // 10
const RING_W = (OUTER_R - INNER_R) / RING_COUNT;
const LABEL_R = OUTER_R + 18;
const CAT_BAND_INNER = OUTER_R + 3;
const CAT_BAND_OUTER = OUTER_R + 9;
const CELL_GAP = 0.35;   // degrees angular gap between cells
const RING_GAP = 0.8;    // px radial gap between rings


// ─── DATA TYPES ─────────────────────────────────────────────────

interface ClusterEntry {
  node: SyntheticNode;
  nodeIndex: number;
  learning: ClusterLearning;
  spokeIndex: number;
  categoryIndex: number;
}

interface CategoryRange {
  name: string;
  hue: number;
  start: number;
  end: number;
}


// ─── MAIN CLASS ─────────────────────────────────────────────────

export class LearnInteraction {
  private panel: HTMLElement;
  private svg: SVGSVGElement;
  private legendAxes: HTMLElement;
  private legendClusters: HTMLElement;
  private legendColors: HTMLElement;

  private panelVisible = false;

  private clusters: ClusterEntry[] = [];
  private categoryRanges: CategoryRange[] = [];
  private learnings: Map<string, ClusterLearning>;

  constructor(
    private container: HTMLElement,
    private camera: THREE.PerspectiveCamera,
    private fieldCamera: FieldCamera,
    private nodeRenderer: NodeRenderer,
    private field: SyntheticField,
    private zoneNavigation: ZoneNavigation,
  ) {
    // Cache DOM
    this.panel = document.getElementById('learn-panel')!;
    this.svg = document.getElementById('learn-svg') as unknown as SVGSVGElement;
    this.legendAxes = document.getElementById('learn-legend-axes')!;
    this.legendClusters = document.getElementById('learn-legend-clusters')!;
    this.legendColors = document.getElementById('learn-legend-colors')!;

    // Generate learn battery data (deterministic, seeded by cluster id)
    this.learnings = generateLearnBatteries();

    // Build id → nodeIndex map for fast lookup
    const nodeMap = new Map<string, number>();
    this.field.nodes.forEach((node, idx) => {
      if (node.id.startsWith('cluster-')) nodeMap.set(node.id, idx);
    });

    // Extract clusters in CATEGORY ORDER (groups around the wheel)
    let spokeIdx = 0;
    CATEGORIES.forEach((cat, catIdx) => {
      const catStart = spokeIdx;
      cat.clusters.forEach((_, clusterIdx) => {
        const id = `cluster-${catIdx}-${clusterIdx}`;
        const nodeIdx = nodeMap.get(id);
        if (nodeIdx !== undefined) {
          const learning = this.learnings.get(id);
          if (learning) {
            this.clusters.push({
              node: this.field.nodes[nodeIdx],
              nodeIndex: nodeIdx,
              learning,
              spokeIndex: spokeIdx,
              categoryIndex: catIdx,
            });
            spokeIdx++;
          }
        }
      });
      if (spokeIdx > catStart) {
        this.categoryRanges.push({
          name: cat.name,
          hue: cat.hue,
          start: catStart,
          end: spokeIdx - 1,
        });
      }
    });

    // Build the visualization
    this.renderWheel();
    this.renderLegend();
  }


  // ─── SVG ARC PATH ────────────────────────────────────────────

  private arcPath(
    innerR: number, outerR: number,
    startDeg: number, endDeg: number,
  ): string {
    const toRad = Math.PI / 180;
    const s = startDeg * toRad;
    const e = endDeg * toRad;

    const x1 = CX + innerR * Math.cos(s);
    const y1 = CY + innerR * Math.sin(s);
    const x2 = CX + innerR * Math.cos(e);
    const y2 = CY + innerR * Math.sin(e);
    const x3 = CX + outerR * Math.cos(e);
    const y3 = CY + outerR * Math.sin(e);
    const x4 = CX + outerR * Math.cos(s);
    const y4 = CY + outerR * Math.sin(s);

    const large = (endDeg - startDeg) > 180 ? 1 : 0;

    return [
      `M ${x1.toFixed(2)} ${y1.toFixed(2)}`,
      `A ${innerR} ${innerR} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`,
      `L ${x3.toFixed(2)} ${y3.toFixed(2)}`,
      `A ${outerR} ${outerR} 0 ${large} 0 ${x4.toFixed(2)} ${y4.toFixed(2)}`,
      'Z',
    ].join(' ');
  }


  // ─── CELL COLOR ──────────────────────────────────────────────

  private getCellColor(battery: LearnBattery, dim: LearnDimension): string {
    const state = battery[dim] as string;
    return LEARN_STATE_COLORS[state] ?? '#6a6a7a';
  }


  // ─── CELL TOOLTIP ────────────────────────────────────────────

  private getCellTooltip(entry: ClusterEntry, dim: LearnDimension): string {
    const state = entry.learning.battery[dim] as string;
    const display = LEARN_STATE_DISPLAY[state] ?? state;
    const label = LEARN_DIMENSION_LABELS[dim];
    return `${entry.node.name}\n${label}: ${display}`;
  }


  // ─── RENDER WHEEL ────────────────────────────────────────────

  private renderWheel() {
    const n = this.clusters.length;
    if (n === 0) return;

    const degPerSpoke = 360 / n;
    const parts: string[] = [];

    // 1 — Concentric ring guides (faint circles)
    for (let r = 0; r <= RING_COUNT; r++) {
      const radius = INNER_R + r * RING_W;
      parts.push(
        `<circle cx="${CX}" cy="${CY}" r="${radius}" fill="none" stroke="rgba(255,255,255,0.03)" stroke-width="0.5"/>`,
      );
    }

    // 2 — Radial spoke guides (very faint)
    for (let i = 0; i < n; i++) {
      const angle = (i * degPerSpoke - 90) * Math.PI / 180;
      const x1 = CX + INNER_R * Math.cos(angle);
      const y1 = CY + INNER_R * Math.sin(angle);
      const x2 = CX + OUTER_R * Math.cos(angle);
      const y2 = CY + OUTER_R * Math.sin(angle);
      parts.push(
        `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" stroke="rgba(255,255,255,0.015)" stroke-width="0.5"/>`,
      );
    }

    // 3 — Category divider lines (slightly thicker)
    for (const cat of this.categoryRanges) {
      const angle = (cat.start * degPerSpoke - 90) * Math.PI / 180;
      const ix = CX + (INNER_R - 4) * Math.cos(angle);
      const iy = CY + (INNER_R - 4) * Math.sin(angle);
      const ox = CX + (OUTER_R + 12) * Math.cos(angle);
      const oy = CY + (OUTER_R + 12) * Math.sin(angle);
      parts.push(
        `<line x1="${ix.toFixed(2)}" y1="${iy.toFixed(2)}" x2="${ox.toFixed(2)}" y2="${oy.toFixed(2)}" stroke="rgba(255,255,255,0.06)" stroke-width="0.8"/>`,
      );
    }

    // 4 — Colored data cells (the core visualization: 60 spokes × 10 rings)
    for (const cluster of this.clusters) {
      const spokeStart = cluster.spokeIndex * degPerSpoke - 90;
      const spokeEnd = (cluster.spokeIndex + 1) * degPerSpoke - 90;

      for (let ringIdx = 0; ringIdx < RING_COUNT; ringIdx++) {
        const dim = LEARN_DIMENSIONS[ringIdx];
        const color = this.getCellColor(cluster.learning.battery, dim);
        const rInner = INNER_R + ringIdx * RING_W + RING_GAP;
        const rOuter = INNER_R + (ringIdx + 1) * RING_W - RING_GAP;

        const path = this.arcPath(rInner, rOuter, spokeStart + CELL_GAP, spokeEnd - CELL_GAP);
        parts.push(
          `<path d="${path}" fill="${color}" opacity="0.78" class="learn-cell" data-spoke="${cluster.spokeIndex}" data-ring="${ringIdx}"/>`,
        );
      }
    }

    // 5 — Category color band (thin arc segments just outside the wheel)
    for (const cat of this.categoryRanges) {
      const startDeg = cat.start * degPerSpoke - 90;
      const endDeg = (cat.end + 1) * degPerSpoke - 90;
      const hsl = `hsl(${Math.round(cat.hue * 360)}, 55%, 55%)`;
      const path = this.arcPath(CAT_BAND_INNER, CAT_BAND_OUTER, startDeg + 0.2, endDeg - 0.2);
      parts.push(`<path d="${path}" fill="${hsl}" opacity="0.55"/>`);
    }

    // 6 — Spoke number labels (around outer edge)
    for (const cluster of this.clusters) {
      const angleDeg = (cluster.spokeIndex + 0.5) * degPerSpoke - 90;
      const angleRad = angleDeg * Math.PI / 180;
      const lx = CX + LABEL_R * Math.cos(angleRad);
      const ly = CY + LABEL_R * Math.sin(angleRad);

      // Radial text, flipped on left half for readability
      let textRot = angleDeg;
      const norm = ((angleDeg % 360) + 360) % 360;
      if (norm > 90 && norm < 270) textRot += 180;

      parts.push(
        `<text x="${lx.toFixed(2)}" y="${ly.toFixed(2)}" transform="rotate(${textRot.toFixed(2)}, ${lx.toFixed(2)}, ${ly.toFixed(2)})" ` +
        `text-anchor="middle" dominant-baseline="central" class="learn-spoke-label" data-spoke="${cluster.spokeIndex}">${cluster.spokeIndex + 1}</text>`,
      );
    }

    // 7 — Ring labels (A–J) at ~195° so they sit in a gap between spokes
    for (let i = 0; i < RING_COUNT; i++) {
      const r = INNER_R + (i + 0.5) * RING_W;
      const angleDeg = 198;
      const angleRad = angleDeg * Math.PI / 180;
      const lx = CX + r * Math.cos(angleRad);
      const ly = CY + r * Math.sin(angleRad);
      parts.push(
        `<text x="${lx.toFixed(2)}" y="${ly.toFixed(2)}" text-anchor="middle" dominant-baseline="central" class="learn-ring-label">${LEARN_DIMENSION_LETTERS[i]}</text>`,
      );
    }

    // 8 — Center text
    parts.push(
      `<text x="${CX}" y="${CY - 16}" text-anchor="middle" dominant-baseline="central" class="learn-center-title">Comparative</text>`,
      `<text x="${CX}" y="${CY}" text-anchor="middle" dominant-baseline="central" class="learn-center-title">Synthesis</text>`,
      `<text x="${CX}" y="${CY + 24}" text-anchor="middle" dominant-baseline="central" class="learn-center-count">${n}</text>`,
      `<text x="${CX}" y="${CY + 40}" text-anchor="middle" dominant-baseline="central" class="learn-center-sub">clusters × 10 dimensions</text>`,
    );

    // Inject SVG content
    this.svg.innerHTML = parts.join('\n');

    // Bind cell + label interactions
    this.bindCellEvents();
  }


  // ─── CELL EVENTS ─────────────────────────────────────────────

  private bindCellEvents() {
    // Tooltip + hover highlight on each data cell
    this.svg.querySelectorAll('.learn-cell').forEach((cell) => {
      const el = cell as SVGPathElement;
      const spokeIdx = parseInt(el.dataset.spoke ?? '-1', 10);
      const ringIdx = parseInt(el.dataset.ring ?? '-1', 10);
      if (spokeIdx < 0 || ringIdx < 0) return;

      const cluster = this.clusters[spokeIdx];
      if (!cluster) return;

      const dim = LEARN_DIMENSIONS[ringIdx];

      // Attach tooltip (one-time setup)
      attachTooltip(el as unknown as HTMLElement, this.getCellTooltip(cluster, dim));

      // Hover → highlight particle + legend
      el.addEventListener('mouseenter', () => {
        useAuraStore.getState().setHoveredNode(cluster.nodeIndex);
        this.highlightLegendCluster(spokeIdx);
      });

      el.addEventListener('mouseleave', () => {
        useAuraStore.getState().setHoveredNode(-1);
        this.clearLegendHighlight();
      });

      // Click → navigate to cluster
      el.addEventListener('click', () => {
        this.zoneNavigation.focusNode(cluster.nodeIndex);
      });
    });

    // Spoke number labels: hover + click
    this.svg.querySelectorAll('.learn-spoke-label').forEach((label) => {
      const el = label as SVGTextElement;
      const spokeIdx = parseInt(el.dataset.spoke ?? '-1', 10);
      if (spokeIdx < 0) return;

      const cluster = this.clusters[spokeIdx];
      if (!cluster) return;

      el.style.cursor = 'pointer';

      attachTooltip(
        el as unknown as HTMLElement,
        `${cluster.spokeIndex + 1}. ${cluster.node.name}\n${cluster.learning.category}`,
      );

      el.addEventListener('mouseenter', () => {
        useAuraStore.getState().setHoveredNode(cluster.nodeIndex);
        this.highlightLegendCluster(spokeIdx);
      });

      el.addEventListener('mouseleave', () => {
        useAuraStore.getState().setHoveredNode(-1);
        this.clearLegendHighlight();
      });

      el.addEventListener('click', () => {
        this.zoneNavigation.focusNode(cluster.nodeIndex);
      });
    });
  }


  // ─── LEGEND ──────────────────────────────────────────────────

  private renderLegend() {
    // Axes section (A–J = 10 Learn dimensions)
    let axesHtml = '';
    LEARN_DIMENSIONS.forEach((dim, i) => {
      axesHtml += `
        <div class="learn-legend-axis-row" data-dim="${dim}">
          <span class="learn-legend-axis-letter">${LEARN_DIMENSION_LETTERS[i]}</span>
          <span class="learn-legend-axis-name">${LEARN_DIMENSION_LABELS[dim]}</span>
        </div>`;
    });
    this.legendAxes.innerHTML = axesHtml;

    // Tooltips on axis rows
    this.legendAxes.querySelectorAll('.learn-legend-axis-row').forEach((row, i) => {
      attachTooltip(row as HTMLElement, LEARN_DIMENSION_DESCRIPTIONS[LEARN_DIMENSIONS[i]]);
    });

    // Color key (7 distinct colors used across all states)
    let colorsHtml = '';
    for (const entry of LEARN_COLOR_LEGEND) {
      colorsHtml += `
        <div class="learn-legend-color-row">
          <span class="learn-legend-color-swatch" style="background: ${entry.color}"></span>
          <span class="learn-legend-color-label">${entry.label}</span>
        </div>`;
    }
    this.legendColors.innerHTML = colorsHtml;

    // Clusters section (numbered, grouped by category)
    let clustersHtml = '';
    for (const catRange of this.categoryRanges) {
      const hsl = `hsl(${Math.round(catRange.hue * 360)}, 55%, 55%)`;
      clustersHtml += `
        <div class="learn-legend-cat-header">
          <span class="learn-legend-cat-dot" style="background: ${hsl}"></span>
          ${catRange.name}
        </div>`;

      for (let s = catRange.start; s <= catRange.end; s++) {
        const cluster = this.clusters[s];
        if (!cluster) continue;
        clustersHtml += `
          <div class="learn-legend-cluster-row" data-legend-spoke="${s}">
            <span class="learn-legend-cluster-num">${s + 1}</span>
            <span class="learn-legend-cluster-name">${cluster.node.name}</span>
          </div>`;
      }
    }
    this.legendClusters.innerHTML = clustersHtml;

    // Bind legend cluster interactions
    this.legendClusters.querySelectorAll('.learn-legend-cluster-row').forEach((row) => {
      const el = row as HTMLElement;
      const spokeIdx = parseInt(el.dataset.legendSpoke ?? '-1', 10);
      if (spokeIdx < 0) return;

      const cluster = this.clusters[spokeIdx];
      if (!cluster) return;

      el.addEventListener('mouseenter', () => {
        useAuraStore.getState().setHoveredNode(cluster.nodeIndex);
        this.highlightWheelSpoke(spokeIdx);
      });

      el.addEventListener('mouseleave', () => {
        useAuraStore.getState().setHoveredNode(-1);
        this.clearWheelHighlight();
      });

      el.addEventListener('click', () => {
        this.zoneNavigation.focusNode(cluster.nodeIndex);
      });
    });
  }


  // ─── HIGHLIGHT HELPERS ───────────────────────────────────────

  private highlightLegendCluster(spokeIdx: number) {
    this.legendClusters.querySelectorAll('.learn-legend-cluster-row').forEach((row) => {
      const el = row as HTMLElement;
      el.classList.toggle('highlighted', el.dataset.legendSpoke === String(spokeIdx));
    });
    // Scroll highlighted row into view
    const hl = this.legendClusters.querySelector('.learn-legend-cluster-row.highlighted') as HTMLElement;
    if (hl) hl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private clearLegendHighlight() {
    this.legendClusters.querySelectorAll('.learn-legend-cluster-row.highlighted').forEach((el) => {
      el.classList.remove('highlighted');
    });
  }

  private highlightWheelSpoke(spokeIdx: number) {
    // Dim all cells except this spoke
    this.svg.querySelectorAll('.learn-cell').forEach((cell) => {
      const el = cell as SVGPathElement;
      el.style.opacity = parseInt(el.dataset.spoke ?? '-1', 10) === spokeIdx ? '1' : '0.2';
    });
    // Brighten this spoke's label, dim others
    this.svg.querySelectorAll('.learn-spoke-label').forEach((label) => {
      const el = label as SVGTextElement;
      el.style.fill = parseInt(el.dataset.spoke ?? '-1', 10) === spokeIdx
        ? 'rgba(255,255,255,0.85)'
        : 'rgba(255,255,255,0.1)';
    });
  }

  private clearWheelHighlight() {
    this.svg.querySelectorAll('.learn-cell').forEach((cell) => {
      (cell as SVGPathElement).style.opacity = '';
    });
    this.svg.querySelectorAll('.learn-spoke-label').forEach((label) => {
      (label as SVGTextElement).style.fill = '';
    });
  }


  // ─── UPDATE (every frame) ────────────────────────────────────

  update() {
    const { mode, modeTransitionProgress } = useAuraStore.getState();
    const inLearn = mode === 'learn';

    if (inLearn && modeTransitionProgress > 0.3) {
      if (!this.panelVisible) {
        this.panelVisible = true;
        this.panel.classList.add('visible');
      }
    } else {
      if (this.panelVisible) {
        this.panelVisible = false;
        this.panel.classList.remove('visible');
        hideTooltip();
      }
    }
  }


  // ─── CLEANUP ─────────────────────────────────────────────────

  dispose() {
    // Event listeners are on child elements that will be GC'd with the panel
  }
}
